import { createPostQuecastAdapter } from '@/utils/communication/postQuecastAdapter'

type Payload = {
    type: string
    timestamp?: number
}

export type Message<T = Payload> = Payload & T

type Listener<P> = (message?: Message<P>) => void

export interface CommunicatorAdapter {
    broadcast<P>(message: Message<P>): void
    listen<P>(listener: (message: Message<P>) => void): void
    stopListening<P>(listener: (message: Message<P>) => void): void
    resetInstance(): void
}

export enum SupportedCommunicator {
    PostQuecast = 'postQuecast',
}

type CommunicatorAdapterParameter = CommunicatorAdapter | SupportedCommunicator

export class CommunicationService {
    private static instance: CommunicationService | null = null
    private communicator: CommunicatorAdapter

    static readonly DEFAULT_COMMUNICATOR = SupportedCommunicator.PostQuecast

    public static getInstance(
        communicator: CommunicatorAdapterParameter = CommunicationService.DEFAULT_COMMUNICATOR,
    ): CommunicationService {
        if (CommunicationService.instance) {
            return CommunicationService.instance
        }

        const adapter = this.createAdapter(communicator)
        CommunicationService.instance = new CommunicationService(adapter)

        return CommunicationService.instance
    }

    public static resetInstance(): void {
        CommunicationService.instance?.resetInstance()
        CommunicationService.instance = null
    }

    private static createAdapter(
        communicator: CommunicatorAdapterParameter,
    ): CommunicatorAdapter {
        if (typeof communicator === 'string') {
            // @todo consider implementing Factory Method or Strategy Pattern for creating different types of adapters when necessary
            if (communicator === CommunicationService.DEFAULT_COMMUNICATOR) {
                return createPostQuecastAdapter()
            }
            throw new Error(`Unsupported communicator adapter: ${communicator}`)
        }
        return communicator
    }

    private constructor(communicator: CommunicatorAdapter) {
        this.communicator = communicator
    }

    resetInstance() {
        this.communicator.resetInstance()
    }

    /**
     * Broadcasts a message to all listeners that are interested in the message type.
     *
     * @param message - The message to be broadcasted. It must conform to the structure
     * defined by the `Message` type, including a 'type' property to categorize the message.
     *
     * @example
     * communicationService.broadcast({ type: 'chatMessage', content: 'Hello World', timestamp: Date.now() });
     */
    broadcast<P = Payload>(message: Message<P>) {
        this.communicator.broadcast(message)
    }

    /**
     * Registers a listener for a specific type of message. The listener will be invoked
     * every time a message of the specified type is broadcasted.
     *
     * @param messageType - The type of message the listener is interested in.
     * @param listener - The callback function to be invoked when a message of the specified type is received.
     *
     * @example
     * communicationService.listen('chatMessage', (message) => console.log(message));
     */
    listen<P = Payload>(messageType: string, listener: Listener<P>) {
        this.communicator.listen((message: Message<P>) => {
            if (message.type !== messageType) {
                return
            }
            listener(message)
        })
    }

    /**
     * Registers a listener for a specific type of message, but only for the next occurrence of that message type.
     * After the listener is invoked once, it is automatically unregistered.
     *
     * @param messageType - The type of message the listener is interested in.
     * @param listener - The callback function to be invoked once when a message of the specified type is received.
     *
     * @example
     * communicationService.listenOnce('chatMessage', (message) => console.log(message));
     */
    listenOnce<P = Payload>(messageType: string, listener: Listener<P>): void {
        const wrappedListener = (message: Message<P>) => {
            if (message.type !== messageType) {
                return
            }
            listener(message)
            this.communicator.stopListening(wrappedListener)
        }

        this.communicator.listen(wrappedListener)
    }
}
